import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import { showError } from '../utils/notifications';
import LoadingIndicator from '../components/LoadingIndicator';
import jsPDF from 'jspdf';

/** Obtiene la fecha actual en Ecuador (America/Guayaquil, UTC-5) en formato YYYY-MM-DD */
function getEcuadorDateStr() {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Guayaquil',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const parts = formatter.formatToParts(new Date());
  const year = parts.find(p => p.type === 'year').value;
  const month = parts.find(p => p.type === 'month').value;
  const day = parts.find(p => p.type === 'day').value;
  return `${year}-${month}-${day}`;
}

function TicketSearch() {
  const location = useLocation();
  const isVentaCA = location.pathname === '/venta-ca';
  const todayStr = getEcuadorDateStr();
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(
    location.pathname === '/venta-ca' ? 'venta-ca' : 'ticket-search'
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState(isVentaCA ? 'ca' : 'plate'); // 'plate' | 'id' | 'ca' | 'zone' | 'plate_moto' | 'plate_auto'
  const [searchDate, setSearchDate] = useState(todayStr); // Fecha actual en formato YYYY-MM-DD
  const isMonitoreo = userRole === 'Monitoreo';
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const userDocRef = doc(db, 'usuarios', currentUser.email);
          const userDoc = await getDoc(userDocRef);
          
          if (!userDoc.exists()) {
            showError("Usuario no encontrado", "Tu perfil no existe en el sistema");
            await auth.signOut();
            navigate('/login');
            return;
          }
          
          setUser(currentUser);
          const userData = userDoc.data();
          setUserRole(userData.rol || 'Usuario');
        } catch (error) {
          console.error("Error al verificar perfil de usuario:", error);
          showError("Error", "Ocurrió un problema al verificar tu perfil");
          await auth.signOut();
          navigate('/login');
        }
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    setActiveSection(location.pathname === '/venta-ca' ? 'venta-ca' : 'ticket-search');
    setSearchType(location.pathname === '/venta-ca' ? 'ca' : 'plate');
    // Forzar fecha del día actual siempre
    setSearchDate(todayStr);
  }, [location.pathname, todayStr]);

  // Para Monitoreo: tipo por defecto placa auto (primero), moto (segundo)
  useEffect(() => {
    if (userRole === 'Monitoreo' && searchType !== 'plate_moto' && searchType !== 'plate_auto') {
      setSearchType('plate_auto');
    }
  }, [userRole, searchType]);

  // Helper function to parse Spanish date format
  const parseSpanishDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') {
      console.log('Fecha inválida:', dateString);
      return null;
    }
    
    try {
      // Manejar formato: "24 de julio de 2025, 7:37:35 a.m. UTC-5"
      if (dateString.includes(' de ')) {
        const parts = dateString.split(',')[0].trim(); // "24 de julio de 2025"
        const timePart = dateString.split(',')[1]?.trim(); // "7:37:35 a.m. UTC-5"
        
        const dateParts = parts.split(' de ');
        
        if (dateParts.length >= 3) {
          const day = parseInt(dateParts[0].trim());
          const monthName = dateParts[1].trim().toLowerCase();
          const year = parseInt(dateParts[2].trim());
          
          const monthMap = {
            'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3,
            'mayo': 4, 'junio': 5, 'julio': 6, 'agosto': 7,
            'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
          };
          
          const month = monthMap[monthName];
          if (month !== undefined) {
            const date = new Date(year, month, day);
            
            // Agregar hora si está disponible
            if (timePart) {
              const timeMatch = timePart.match(/(\d{1,2}):(\d{2}):(\d{2})\s*([ap]\.\s*m\.)/i);
              if (timeMatch) {
                let [, hours, minutes, seconds, meridiem] = timeMatch;
                hours = parseInt(hours);
                if (meridiem.toLowerCase().includes('p') && hours < 12) {
                  hours += 12;
                } else if (meridiem.toLowerCase().includes('a') && hours === 12) {
                  hours = 0;
                }
                date.setHours(hours, parseInt(minutes), parseInt(seconds));
              }
            }
            
            console.log('Fecha parseada (formato español):', {
              original: dateString,
              parsed: date.toLocaleString('es-ES')
            });
            
            return date;
          }
        }
      }
      
      // Manejar formato: "07:37 24/07/2025"
      const dateTimeMatch = dateString.match(/(\d{2}):(\d{2})\s+(\d{2})\/(\d{2})\/(\d{4})/);
      if (dateTimeMatch) {
        const [, hours, minutes, day, month, year] = dateTimeMatch;
        const date = new Date(year, parseInt(month) - 1, day, hours, minutes);
        
        console.log('Fecha parseada (formato DD/MM/YYYY):', {
          original: dateString,
          parsed: date.toLocaleString('es-ES')
        });
        
        return date;
      }
      
      // Intentar parsear como fecha ISO o timestamp de Firestore
      if (typeof dateString === 'object' && dateString.seconds) {
        const date = new Date(dateString.seconds * 1000);
        console.log('Fecha parseada (Firestore timestamp):', {
          original: dateString,
          parsed: date.toLocaleString('es-ES')
        });
        return date;
      }
      
      const isoDate = new Date(dateString);
      if (!isNaN(isoDate.getTime())) {
        console.log('Fecha parseada (formato ISO):', {
          original: dateString,
          parsed: isoDate.toLocaleString('es-ES')
        });
        return isoDate;
      }
      
      console.log('No se pudo parsear la fecha:', dateString);
      return null;
      
    } catch (error) {
      console.warn('Error parsing date:', dateString, error);
      return null;
    }
  };

  // Función para buscar tickets en la nueva estructura por fecha
  const searchTickets = async (e) => {
    if (e) e.preventDefault();
    
    if (!searchQuery.trim()) {
      showError("Búsqueda Vacía", "Por favor ingrese un valor para buscar");
      return;
    }
    
    // Monitoreo siempre usa fecha actual; otros roles usan searchDate
    const dateToUse = isMonitoreo ? todayStr : searchDate;
    if (!dateToUse) {
      showError("Fecha requerida", "Por favor seleccione una fecha para buscar");
      return;
    }
    
    setSearching(true);
    setSearchResults([]);
    setSelectedTicket(null);
    setSearchPerformed(true);
    
    try {
      console.log("🔍 Iniciando búsqueda:", { searchType, searchQuery, dateToUse });
      
      // Construir la ruta de la colección con la fecha seleccionada
      const ticketsPath = `ticketsVendidos/${dateToUse}/tickets`;
      const ticketsRef = collection(db, ticketsPath);
      console.log("📚 Referencia a colección creada:", ticketsPath);
      
      let q;
      const fetchedTickets = [];
      
      if (searchType === 'id') {
        // Buscar por ID específico (document ID)
        const formattedId = searchQuery.trim();
        console.log("Buscando ID específico:", formattedId);
        
        try {
          const docRef = doc(db, ticketsPath, formattedId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            console.log("📄 Ticket encontrado por ID:", docSnap.id);
            const docData = docSnap.data();
            
            // Procesar coordenadas
            let coordinates = null;
            if (typeof docData.latitude === 'number' && typeof docData.longitude === 'number') {
              coordinates = { latitude: docData.latitude, longitude: docData.longitude };
            } else if (docData.coordinates && typeof docData.coordinates === 'string') {
              const [lat, lng] = docData.coordinates.split(',').map(Number);
              if (!isNaN(lat) && !isNaN(lng)) {
                coordinates = { latitude: lat, longitude: lng };
              }
            }

            if (coordinates && (isNaN(coordinates.latitude) || isNaN(coordinates.longitude))) {
              coordinates = null;
            }

            const singleTicket = {
              id: docSnap.id,
              ...docData,
              formattedStartTime: formatTimestamp(docData.startTime || docData.createdAt),
              formattedEndTime: formatTimestamp(docData.endTime),
              formattedCreatedAt: formatTimestamp(docData.createdAt),
              isActive: isTicketActive(docData.endTime || docData.status),
              coordinates: coordinates,
            };

            fetchedTickets.push(singleTicket);
          } else {
            console.log("❌ No se encontró ticket con ID:", formattedId);
          }
        } catch (docError) {
          console.warn("⚠️ Error buscando por document ID:", docError);
        }
      } else if (searchType === 'ca') {
        // Buscar por número de CA
        const formattedCA = searchQuery.toUpperCase().trim();
        console.log("Buscando por número CA:", formattedCA);

        q = query(
          ticketsRef,
          where("numberCa", "==", formattedCA)
        );

        console.log("📋 Ejecutando consulta por CA...");
        const querySnapshot = await getDocs(q);
        console.log("📊 Documentos encontrados:", querySnapshot.size);

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          fetchedTickets.push({
            id: doc.id,
            ...data,
            formattedStartTime: formatTimestamp(data.startTime || data.createdAt),
            formattedEndTime: formatTimestamp(data.endTime),
            formattedCreatedAt: formatTimestamp(data.createdAt),
            isActive: isTicketActive(data.endTime || data.status),
            coordinates: parseCoordinates(data),
          });
        });
      } else if (searchType === 'zone') {
        // Buscar por zona (campo zone o zona)
        const formattedZone = searchQuery.trim();
        console.log("Buscando por zona:", formattedZone);

        const zoneQueries = [
          query(ticketsRef, where("zone", "==", formattedZone)),
          query(ticketsRef, where("zona", "==", formattedZone)),
        ];

        for (const zoneQuery of zoneQueries) {
          const zoneSnapshot = await getDocs(zoneQuery);
          zoneSnapshot.forEach((doc) => {
            const data = doc.data();
            fetchedTickets.push({
              id: doc.id,
              ...data,
              formattedStartTime: formatTimestamp(data.startTime || data.createdAt),
              formattedEndTime: formatTimestamp(data.endTime),
              formattedCreatedAt: formatTimestamp(data.createdAt),
              isActive: isTicketActive(data.endTime || data.status),
              coordinates: parseCoordinates(data),
            });
          });
        }
      } else {
        // Buscar por placa (incluye plate, plate_moto, plate_auto)
        const formattedPlate = searchQuery.toUpperCase().trim();
        console.log("Buscando placa:", formattedPlate);
        
        q = query(
          ticketsRef,
          where("plate", "==", formattedPlate)
        );
        
        console.log("📋 Ejecutando consulta por placa...");
        const querySnapshot = await getDocs(q);
        console.log("📊 Documentos encontrados:", querySnapshot.size);
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          
          fetchedTickets.push({
            id: doc.id,
            ...data,
            formattedStartTime: formatTimestamp(data.startTime || data.createdAt),
            formattedEndTime: formatTimestamp(data.endTime),
            formattedCreatedAt: formatTimestamp(data.createdAt),
            isActive: isTicketActive(data.endTime || data.status),
            coordinates: parseCoordinates(data),
          });
        });

        // Monitoreo: filtrar por tipo de vehículo (moto o auto)
        if (searchType === 'plate_moto' || searchType === 'plate_auto') {
          const filtered = searchType === 'plate_moto'
            ? fetchedTickets.filter((t) => isMoto(t.vehicleType))
            : fetchedTickets.filter((t) => isAuto(t.vehicleType));
          fetchedTickets.length = 0;
          fetchedTickets.push(...filtered);
        }
      }
      
      console.log(`✅ Total tickets procesados: ${fetchedTickets.length}`);

      // Eliminar duplicados si provinieron de múltiples consultas (zona)
      const uniqueById = [];
      const seenIds = new Set();
      fetchedTickets.forEach((t) => {
        if (!seenIds.has(t.id)) {
          seenIds.add(t.id);
          uniqueById.push(t);
        }
      });

      // Ordenar por fecha de creación (más reciente primero)
      // Ordenar descendente por fecha comparable (creación preferida)
      uniqueById.sort((a, b) => getComparableTimestamp(b) - getComparableTimestamp(a));

      console.log(`🎯 Resultados finales: ${uniqueById.length}`);
      setSearchResults(uniqueById);
      
      if (uniqueById.length === 0) {
        showError(
          "Sin resultados", 
          `No se encontraron tickets para la búsqueda "${searchQuery}" en la fecha ${dateToUse}`
        );
      } else if (uniqueById.length === 1) {
        setSelectedTicket(uniqueById[0]);
      }
      
    } catch (error) {
      console.error("❌ Error completo al buscar tickets:", error);
      console.error("Error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      
      // Mostrar error más específico
      let errorMessage = "No se pudo realizar la búsqueda";
      if (error.code === 'failed-precondition') {
        errorMessage = "Error de índices en la base de datos. Por favor contacte al administrador.";
      } else if (error.code === 'permission-denied') {
        errorMessage = "No tiene permisos para realizar esta búsqueda.";
      } else if (error.message.includes('offline')) {
        errorMessage = "Sin conexión a internet. Verifique su conexión.";
      }
      
      showError("Error de Búsqueda", errorMessage);
    } finally {
      setSearching(false);
    }
  };

  // Format timestamp to show date and time
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      
      // Manejar timestamp de Firestore
      if (typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } 
      // Manejar string en formato "07:37 24/07/2025"
      else if (typeof timestamp === 'string' && timestamp.includes('/')) {
        const dateTimeMatch = timestamp.match(/(\d{2}):(\d{2})\s+(\d{2})\/(\d{2})\/(\d{4})/);
        if (dateTimeMatch) {
          const [, hours, minutes, day, month, year] = dateTimeMatch;
          date = new Date(year, parseInt(month) - 1, day, hours, minutes);
        } else {
          date = new Date(timestamp);
        }
      } 
      // Manejar objeto Date
      else if (timestamp instanceof Date) {
        date = timestamp;
      } 
      // Intentar parsear como string
      else {
        date = new Date(timestamp);
      }
      
      // Verificar si la fecha es válida
      if (isNaN(date.getTime())) {
        return String(timestamp);
      }
      
      // Format as DD/MM/YYYY, HH:MM
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      return `${day}/${month}/${year}, ${hours}:${minutes}`;
    } catch (error) {
      console.error("Error formateando fecha:", error);
      return String(timestamp);
    }
  };

  // Check if a ticket is active
  const isTicketActive = (endTimeOrStatus) => {
    if (!endTimeOrStatus) return false;
    
    try {
      // Si es un status string, verificar directamente
      if (typeof endTimeOrStatus === 'string' && (endTimeOrStatus === 'active' || endTimeOrStatus === 'inactive')) {
        return endTimeOrStatus === 'active';
      }
      
      let endDate;

      // Intentar parsear como string HH:MM DD/MM/YYYY
      if (typeof endTimeOrStatus === 'string') {
        const dateTimeMatch = endTimeOrStatus.match(/(\d{2}):(\d{2})\s+(\d{2})\/(\d{2})\/(\d{4})/);
        if (dateTimeMatch) {
          const [, hours, minutes, day, month, year] = dateTimeMatch;
          endDate = new Date(year, parseInt(month) - 1, day, hours, minutes);
        } else {
           // Si no coincide el formato string, intentar parsear como ISO u otro formato estándar
           endDate = new Date(endTimeOrStatus);
        }

      } else if (typeof endTimeOrStatus === 'object' && endTimeOrStatus.seconds) {
        // Manejar Timestamp de Firestore
        endDate = new Date(endTimeOrStatus.seconds * 1000);
      } else if (endTimeOrStatus instanceof Date) {
        // Manejar objeto Date nativo
        endDate = endTimeOrStatus;
      } else {
        console.warn("Formato de fecha de fin desconocido:", endTimeOrStatus);
        return false; // Formato desconocido, considerar inactivo
      }
      
      // Verificar si la fecha parseada es válida
      if (isNaN(endDate.getTime())) {
        console.warn("Fecha de fin parseada inválida:", endTimeOrStatus);
        return false; // Fecha inválida, considerar inactivo
      }

      // Comparar con la hora actual
      const now = new Date();
      return now < endDate;
    } catch (error) {
      console.error("Error al verificar si el ticket está activo:", endTimeOrStatus, error);
      return false; // Error, considerar inactivo
    }
  };

  // Select a ticket to view details
  const viewTicketDetails = (ticket) => {
    setSelectedTicket(ticket);
  };

  // Scroll al panel de detalles cuando se selecciona un ticket
  useEffect(() => {
    if (selectedTicket) {
      const el = document.getElementById('ticket-details');
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedTicket]);

  // Go back to search results
  const backToResults = () => {
    setSelectedTicket(null);
  };

  // Normaliza coordenadas desde distintos formatos
  const parseCoordinates = (data) => {
    if (typeof data?.latitude === 'number' && typeof data?.longitude === 'number') {
      return { latitude: data.latitude, longitude: data.longitude };
    }
    if (data?.coordinates && typeof data.coordinates === 'string') {
      const [lat, lng] = data.coordinates.split(',').map(Number);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { latitude: lat, longitude: lng };
      }
    }
    return null;
  };

  const isMoto = (type = '') => {
    const normalized = String(type).toLowerCase();
    return normalized === 'moto' || normalized === 'motocicleta';
  };

  const isAuto = (type = '') => {
    const normalized = String(type).toLowerCase();
    return normalized === 'auto' || normalized === 'automovil' || normalized === 'automóvil';
  };

  // Devuelve timestamp numérico para ordenar (desc) usando createdAt o startTime
  const getComparableTimestamp = (ticket) => {
    const source = ticket?.createdAt || ticket?.startTime || ticket?.created_at;
    if (!source) return 0;

    // Firestore timestamp
    if (typeof source === 'object' && source.seconds) {
      return source.seconds * 1000;
    }

    // String / Date
    const parsed = parseSpanishDate(source) || new Date(source);
    if (!isNaN(parsed?.getTime())) {
      return parsed.getTime();
    }

    return 0;
  };

  // Generate and download PDF for ticket
  const generatePDF = (ticket) => {
    try {
      const doc = new jsPDF();
      
      // Configurar fuente y colores
      doc.setFont("helvetica");
      doc.setFontSize(20);
      
      // Título principal
      doc.setTextColor(0, 0, 0);
      doc.text("COMPROBANTE DE TICKET", 105, 20, { align: "center" });
      
      // Línea separadora
      doc.setDrawColor(0, 0, 0);
      doc.line(20, 25, 190, 25);
      
      // Información del ticket
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      
      let yPosition = 40;
      
      // ID del Ticket
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("ID del Ticket:", 20, yPosition);
      doc.setFont("helvetica", "normal");
      doc.text(ticket.id || 'N/A', 60, yPosition);
      yPosition += 10;
      
      // Placa
      doc.setFont("helvetica", "bold");
      doc.text("Placa:", 20, yPosition);
      doc.setFont("helvetica", "normal");
      doc.text(ticket.plate || 'N/A', 60, yPosition);
      yPosition += 10;
      
      // Tipo de vehículo
      doc.setFont("helvetica", "bold");
      doc.text("Tipo de Vehículo:", 20, yPosition);
      doc.setFont("helvetica", "normal");
      const vehicleType = ticket.vehicleType === 'auto' || ticket.vehicleType === 'Automovil' ? 'Automóvil' : 
                         ticket.vehicleType === 'moto' || ticket.vehicleType === 'Motocicleta' ? 'Motocicleta' : 
                         ticket.vehicleType === 'exonerado' ? 'Exonerado' : ticket.vehicleType || 'Vehículo';
      doc.text(vehicleType, 60, yPosition);
      yPosition += 10;
      
      // Estado
      doc.setFont("helvetica", "bold");
      doc.text("Estado:", 20, yPosition);
      doc.setFont("helvetica", "normal");
      if (ticket.isActive) {
        doc.setTextColor(0, 128, 0);
        doc.text("ACTIVO", 60, yPosition);
      } else {
        doc.setTextColor(255, 0, 0);
        doc.text("EXPIRADO", 60, yPosition);
      }
      doc.setTextColor(0, 0, 0);
      yPosition += 15;
      
      // Período de validez
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("PERÍODO DE VALIDEZ", 105, yPosition, { align: "center" });
      yPosition += 10;
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Inicio:", 20, yPosition);
      doc.setFont("helvetica", "normal");
      doc.text(ticket.formattedStartTime || 'N/A', 60, yPosition);
      yPosition += 10;
      
      doc.setFont("helvetica", "bold");
      doc.text("Fin:", 20, yPosition);
      doc.setFont("helvetica", "normal");
      doc.text(ticket.formattedEndTime || 'N/A', 60, yPosition);
      yPosition += 15;
      
      // Información adicional
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("INFORMACIÓN ADICIONAL", 105, yPosition, { align: "center" });
      yPosition += 10;
      
      doc.setFontSize(12);
      
      // Zona
      if (ticket.zone || ticket.zona) {
        doc.setFont("helvetica", "bold");
        doc.text("Zona:", 20, yPosition);
        doc.setFont("helvetica", "normal");
        doc.text(ticket.zone || ticket.zona, 60, yPosition);
        yPosition += 10;
      }
      
      // Precio
      if (ticket.price) {
        doc.setFont("helvetica", "bold");
        doc.text("Precio:", 20, yPosition);
        doc.setFont("helvetica", "normal");
        doc.text(`$${ticket.price}`, 60, yPosition);
        yPosition += 10;
      }
      
      // Vendedor
      if (ticket.nombre) {
        doc.setFont("helvetica", "bold");
        doc.text("Vendedor:", 20, yPosition);
        doc.setFont("helvetica", "normal");
        doc.text(ticket.nombre, 60, yPosition);
        yPosition += 10;
      }
      
      // Número CA
      if (ticket.numberCa) {
        doc.setFont("helvetica", "bold");
        doc.text("Número CA:", 20, yPosition);
        doc.setFont("helvetica", "normal");
        doc.text(ticket.numberCa, 60, yPosition);
        yPosition += 10;
      }
      
      // Período del ticket
      if (ticket.ticketPeriod) {
        doc.setFont("helvetica", "bold");
        doc.text("Período:", 20, yPosition);
        doc.setFont("helvetica", "normal");
        doc.text(ticket.ticketPeriod, 60, yPosition);
        yPosition += 10;
      }
      
      // Ubicación si está disponible
      if (ticket.coordinates && ticket.coordinates.latitude && ticket.coordinates.longitude) {
        yPosition += 5;
        doc.setFont("helvetica", "bold");
        doc.text("Ubicación:", 20, yPosition);
        doc.setFont("helvetica", "normal");
        doc.text(`Lat: ${ticket.coordinates.latitude.toFixed(6)}`, 60, yPosition);
        yPosition += 8;
        doc.text(`Lng: ${ticket.coordinates.longitude.toFixed(6)}`, 60, yPosition);
        yPosition += 10;
      }
      
      // Fecha de creación
      yPosition += 5;
      doc.setFont("helvetica", "bold");
      doc.text("Fecha de Creación:", 20, yPosition);
      doc.setFont("helvetica", "normal");
      doc.text(ticket.formattedCreatedAt || 'N/A', 60, yPosition);
      
      // Pie de página con texto oficial
      const pageHeight = doc.internal.pageSize.height;
      doc.setFontSize(10);
      doc.setTextColor(128, 128, 128);
      doc.text("Este documento ha sido generado directamente desde la base de datos de Parqueo Amigo", 105, pageHeight - 25, { align: "center" });
      doc.text("y contiene información oficial del sistema de parqueo municipal.", 105, pageHeight - 20, { align: "center" });
      doc.text(`Fecha de impresión: ${new Date().toLocaleString('es-ES')}`, 105, pageHeight - 15, { align: "center" });
      
      // Descargar el PDF
      const fileName = `ticket_${ticket.plate || 'unknown'}_${ticket.id || 'unknown'}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error("Error generando PDF:", error);
      showError("Error", "No se pudo generar el PDF. Intente nuevamente.");
    }
  };

  const pageTitle = location.pathname === '/venta-ca' ? 'Venta CA' : 'Consulta de Tickets';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <LoadingIndicator size="large" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Mobile Header */}
      <MobileHeader 
        title={pageTitle} 
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      
      {/* Top spacing for mobile */}
      <div className="md:hidden h-14"></div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Mobile menu overlay */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}
        
        {/* Sidebar */}
        <Sidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection} 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {/* Desktop Header */}
          <header className="bg-white shadow px-6 py-4 hidden md:block">
            <div className="flex justify-between items-center">
              <h1 className="text-xl font-bold text-gray-900">{pageTitle}</h1>
              <div className="text-right flex items-center">
                <div className="mr-4">
                  <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                  <p className="text-xs text-gray-500">{userRole}</p>
                </div>
                <button 
                  onClick={async () => {
                    try {
                      localStorage.removeItem('userRole');
                      await auth.signOut();
                      navigate('/login');
                    } catch (error) {
                      console.error("Error al cerrar sesión:", error);
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1 rounded"
                >
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className={`p-4 md:p-6 flex flex-col ${isMonitoreo ? 'min-h-[calc(100vh-8rem)]' : ''}`}>
            <>
                {/* Search form - Monitoreo: diseño amplio, sin fecha, placa moto/auto */}
                {isMonitoreo ? (
                  <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-6 max-w-2xl mx-auto w-full">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-1">Consulta de Tickets</h2>
                    <p className="text-sm text-gray-500 mb-6">
                      Buscar por placa de moto o auto (fecha actual)
                    </p>
                    
                    <form onSubmit={searchTickets} className="space-y-6">
                      {/* Tipo: Placa auto (1°) / Placa moto (2°) */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        <label className={`inline-flex items-center justify-center p-4 rounded-xl flex-1 cursor-pointer transition-colors border-2 ${
                          searchType === 'plate_auto'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-transparent bg-gray-100 hover:bg-gray-200'
                        }`}>
                          <input
                            type="radio"
                            name="searchTypeMonitoreo"
                            value="plate_auto"
                            checked={searchType === 'plate_auto'}
                            onChange={() => setSearchType('plate_auto')}
                            className="form-radio h-5 w-5 text-blue-600 sr-only"
                          />
                          <span className="text-lg font-medium text-gray-700 ml-2">🚗 Placa de auto</span>
                        </label>
                        <label className={`inline-flex items-center justify-center p-4 rounded-xl flex-1 cursor-pointer transition-colors border-2 ${
                          searchType === 'plate_moto'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-transparent bg-gray-100 hover:bg-gray-200'
                        }`}>
                          <input
                            type="radio"
                            name="searchTypeMonitoreo"
                            value="plate_moto"
                            checked={searchType === 'plate_moto'}
                            onChange={() => setSearchType('plate_moto')}
                            className="form-radio h-5 w-5 text-blue-600 sr-only"
                          />
                          <span className="text-lg font-medium text-gray-700 ml-2">🏍️ Placa de moto</span>
                        </label>
                      </div>

                      {/* Input grande para placa */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder={
                            searchType === 'plate_moto'
                              ? "Ingrese placa de moto (ej: PDV-3792)"
                              : "Ingrese placa de auto (ej: ABC-1234)"
                          }
                          className="flex-1 border-2 border-gray-300 rounded-xl shadow-sm py-4 px-5 text-xl md:text-2xl font-medium placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                          required
                          autoCapitalize="characters"
                          autoComplete="off"
                          autoCorrect="off"
                        />
                        <button
                          type="submit"
                          className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-xl flex items-center justify-center font-semibold text-lg min-w-[140px]"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          Buscar
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  /* Search form estándar - Incluye selector de fecha */
                  <div className="bg-white rounded-lg shadow-md p-4 mb-4">
                    <h2 className="text-lg font-semibold text-gray-700 mb-1">Buscar Ticket</h2>
                    <p className="text-sm text-gray-500 mb-3">
                      Buscar ticket por placa o ID en una fecha específica
                    </p>
                    
                    <form onSubmit={searchTickets} className="space-y-3">
                      {/* Date selector */}
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fecha de búsqueda
                        </label>
                        <input
                          type="date"
                          value={searchDate}
                          onChange={(e) => setSearchDate(e.target.value)}
                          min={todayStr}
                          max={todayStr}
                          className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                          required
                          disabled
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Solo se puede buscar en la fecha actual
                        </p>
                      </div>

                      {/* Search type selector */}
                      <div className="flex space-x-4 justify-center mb-2">
                        {isVentaCA ? (
                          <>
                            <label className="inline-flex items-center bg-gray-100 p-2 rounded-lg flex-1 justify-center">
                              <input
                                type="radio"
                                name="searchType"
                                value="ca"
                                checked={searchType === 'ca'}
                                onChange={() => setSearchType('ca')}
                                className="form-radio h-4 w-4 text-blue-600"
                              />
                              <span className="ml-2 text-gray-700">Buscar por CA</span>
                            </label>
                            <label className="inline-flex items-center bg-gray-100 p-2 rounded-lg flex-1 justify-center">
                              <input
                                type="radio"
                                name="searchType"
                                value="zone"
                                checked={searchType === 'zone'}
                                onChange={() => setSearchType('zone')}
                                className="form-radio h-4 w-4 text-blue-600"
                              />
                              <span className="ml-2 text-gray-700">Buscar por Zona</span>
                            </label>
                          </>
                        ) : (
                          <>
                            <label className="inline-flex items-center bg-gray-100 p-2 rounded-lg flex-1 justify-center">
                              <input
                                type="radio"
                                name="searchType"
                                value="plate"
                                checked={searchType === 'plate'}
                                onChange={() => setSearchType('plate')}
                                className="form-radio h-4 w-4 text-blue-600"
                              />
                              <span className="ml-2 text-gray-700">Buscar por placa</span>
                            </label>
                            <label className="inline-flex items-center bg-gray-100 p-2 rounded-lg flex-1 justify-center">
                              <input
                                type="radio"
                                name="searchType"
                                value="id"
                                checked={searchType === 'id'}
                                onChange={() => setSearchType('id')}
                                className="form-radio h-4 w-4 text-blue-600"
                              />
                              <span className="ml-2 text-gray-700">Buscar por ID</span>
                            </label>
                          </>
                        )}
                      </div>

                      <div className="flex">
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder={
                            isVentaCA
                              ? (searchType === 'zone'
                                  ? "Ingrese zona (ej: Z 7-S06)"
                                  : "Ingrese número CA (ej: CA197)")
                              : (searchType === 'plate'
                                  ? "Ingrese placa (ej: PDV-3792)"
                                  : "Ingrese ID del ticket (ej: 1102013)")
                          }
                          className="w-full border border-gray-300 rounded-l-md shadow-sm py-2 px-3 text-lg"
                          required
                          autoCapitalize={searchType === 'plate' || searchType === 'ca' ? "characters" : "none"}
                          autoComplete="off"
                          autoCorrect="off"
                        />
                        <button
                          type="submit"
                          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-r-md flex items-center"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Loading indicator during search */}
                {searching && (
                  <div className="flex justify-center my-8">
                    <LoadingIndicator size="medium" />
                  </div>
                )}

                {/* Contenedor: Monitoreo = detalles arriba, Tickets Encontrados abajo */}
                <div className={isMonitoreo ? 'mt-auto w-full flex flex-col items-center' : ''}>
                {/* Detalles del ticket - PRIMERO para Monitoreo (datos más visibles, debajo del buscador) */}
                {selectedTicket && (
                  <div className={`${isMonitoreo ? 'mb-6' : 'mt-6'} w-full max-w-3xl mx-auto`} id="ticket-details">
                    <div className="bg-white rounded-xl shadow-xl border-2 border-gray-200 overflow-hidden">
                      <div className={`px-6 py-5 ${
                        selectedTicket.isActive ? 'bg-green-100 border-b-2 border-green-200' : 'bg-red-100 border-b-2 border-red-200'
                      }`}>
                        <div className="flex justify-between items-center flex-wrap gap-3">
                          <div>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                              {selectedTicket.vehicleType === 'auto' || selectedTicket.vehicleType === 'Automovil' ? '🚗 ' : 
                               selectedTicket.vehicleType === 'moto' || selectedTicket.vehicleType === 'Motocicleta' ? '🏍️ ' : 
                               selectedTicket.vehicleType === 'exonerado' ? '🛡️ ' : '🚘 '}
                              {selectedTicket.plate}
                            </h2>
                            <p className="text-lg font-semibold text-gray-700 mt-1">
                              {selectedTicket.vehicleType === 'auto' || selectedTicket.vehicleType === 'Automovil' ? 'Automóvil' : 
                               selectedTicket.vehicleType === 'moto' || selectedTicket.vehicleType === 'Motocicleta' ? 'Motocicleta' : 
                               selectedTicket.vehicleType === 'exonerado' ? 'Exonerado' : selectedTicket.vehicleType || 'Vehículo'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-4 py-2 rounded-lg text-sm font-bold ${
                              selectedTicket.isActive ? 'bg-green-200 text-green-900' : 'bg-red-200 text-red-900'
                            }`}>
                              {selectedTicket.isActive ? 'ACTIVO' : 'EXPIRADO'}
                            </span>
                            <button
                              onClick={backToResults}
                              className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium"
                            >
                              Cerrar
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-6">
                        <div className="mb-6">
                          <h3 className="text-sm font-semibold text-gray-600 mb-2">Período de validez</h3>
                          <div className="flex items-center bg-blue-50 p-4 rounded-xl border border-blue-100">
                            <div className="flex-1">
                              <p className="text-xs text-gray-500 uppercase">Inicio</p>
                              <p className="text-lg font-bold text-gray-900">{selectedTicket.formattedStartTime}</p>
                            </div>
                            <div className="px-6 text-2xl text-gray-400">→</div>
                            <div className="flex-1">
                              <p className="text-xs text-gray-500 uppercase">Fin</p>
                              <p className="text-lg font-bold text-gray-900">{selectedTicket.formattedEndTime}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Tiempo restante</p>
                            <p className="text-xl font-bold text-gray-900">
                              {(() => {
                                const now = new Date();
                                const endTime = selectedTicket.endTime;
                                let endDate;
                                try {
                                  if (typeof endTime === 'string') {
                                    const dateTimeMatch = endTime.match(/(\d{2}):(\d{2})\s+(\d{2})\/(\d{2})\/(\d{4})/);
                                    if (dateTimeMatch) {
                                      const [, hours, minutes, day, month, year] = dateTimeMatch;
                                      endDate = new Date(year, parseInt(month) - 1, day, hours, minutes);
                                    } else endDate = new Date(endTime);
                                    } else if (typeof endTime === 'object' && endTime.seconds) {
                                      endDate = new Date(endTime.seconds * 1000);
                                    } else if (endTime instanceof Date) {
                                      endDate = endTime;
                                    } else return 'N/A';
                                  if (isNaN(endDate.getTime())) return 'Inválida';
                                  const diffMs = endDate.getTime() - now.getTime();
                                  const diffMinutes = Math.ceil(diffMs / (1000 * 60));
                                  return diffMinutes <= 0 ? 'Expirado' : `${diffMinutes} min`;
                                } catch (e) { return 'Error'; }
                              })()}
                            </p>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">ID del ticket</p>
                            <p className="text-base font-bold text-gray-900 break-all">{selectedTicket.id}</p>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Zona</p>
                            <p className="text-base font-bold text-gray-900">{selectedTicket.zone || selectedTicket.zona || '—'}</p>
                          </div>
                          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Precio</p>
                            <p className="text-base font-bold text-gray-900">${selectedTicket.price || 'N/A'}</p>
                          </div>
                          {selectedTicket.nombre && (
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Vendedor</p>
                              <p className="text-base font-bold text-gray-900">{selectedTicket.nombre}</p>
                            </div>
                          )}
                          {selectedTicket.numberCa && (
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Número CA</p>
                              <p className="text-base font-bold text-gray-900">{selectedTicket.numberCa}</p>
                            </div>
                          )}
                        </div>
                        
                        {selectedTicket.coordinates && (
                          <div className="mt-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Ubicación</p>
                            <p className="text-sm text-gray-600 mb-3">
                              Lat: {selectedTicket.coordinates.latitude?.toFixed(6) || 'N/A'}, Lng: {selectedTicket.coordinates.longitude?.toFixed(6) || 'N/A'}
                            </p>
                            <a 
                              href={`https://www.google.com/maps/search/?api=1&query=${selectedTicket.coordinates.latitude},${selectedTicket.coordinates.longitude}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-block bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg font-medium"
                            >
                              Ver en Google Maps
                            </a>
                          </div>
                        )}
                      </div>
                      
                      <div className="px-6 py-4 bg-blue-50 border-t border-blue-100">
                        <button
                          onClick={() => generatePDF(selectedTicket)}
                          className="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-xl flex items-center justify-center font-semibold text-lg"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Descargar Comprobante PDF
                        </button>
                      </div>
                      
                      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 text-sm text-gray-500">
                        Ticket creado el {selectedTicket.formattedCreatedAt}
                        {selectedTicket.ticketPeriod && ` • Período: ${selectedTicket.ticketPeriod}`}
                      </div>
                    </div>
                  </div>
                )}

                {/* Search results - Tickets Encontrados (al final para Monitoreo) */}
                {searchPerformed && !searching && searchResults.length > 0 && (() => {
                  const autos = searchResults.filter((t) => isAuto(t.vehicleType));
                  const motos = searchResults.filter((t) => isMoto(t.vehicleType));
                  const otros = searchResults.filter((t) => !isAuto(t.vehicleType) && !isMoto(t.vehicleType));

                  const Section = ({ title, data }) => {
                    if (!data.length) return null;
                    return (
                      <div className="divide-y divide-gray-200">
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 font-semibold text-gray-700">
                          {title} ({data.length})
                        </div>
                        {data.map((ticket) => (
                          <div 
                            key={ticket.id} 
                            className="p-4 hover:bg-gray-50 cursor-pointer"
                            onClick={() => viewTicketDetails(ticket)}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-bold text-lg text-gray-900">{ticket.plate}</p>
                                <p className="text-sm text-gray-600">
                                  {isAuto(ticket.vehicleType) ? '🚗 Auto' : 
                                  isMoto(ticket.vehicleType) ? '🏍️ Moto' : 
                                  ticket.vehicleType === 'exonerado' ? '🛡️ Exonerado' : '🚘 ' + (ticket.vehicleType || 'Vehículo')}
                                </p>
                                <p className="text-sm text-gray-500">
                                  📍 {ticket.zone || ticket.zona || 'Zona no disponible'}
                                </p>
                                {ticket.nombre && (
                                  <p className="text-sm text-gray-500">
                                    👤 {ticket.nombre}
                                  </p>
                                )}
                              </div>
                              <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                                ticket.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {ticket.isActive ? 'ACTIVO' : 'EXPIRADO'}
                              </span>
                            </div>
                            <div className="mt-2 flex justify-between">
                              <div className="text-sm">
                                <p className="text-gray-500">Inicia: {ticket.formattedStartTime}</p>
                                <p className="text-gray-500">Finaliza: {ticket.formattedEndTime}</p>
                                {ticket.price && (
                                  <p className="text-gray-500">Precio: ${ticket.price}</p>
                                )}
                              </div>
                              <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                                Ver detalles →
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  };

                  return (
                    <div className={`bg-white rounded-lg shadow-md overflow-hidden ${isMonitoreo ? 'w-full max-w-3xl' : ''}`}>
                      <div className="px-4 py-3 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-700">
                          Tickets Encontrados ({searchResults.length}) - {searchDate}
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                          Autos: {autos.length} • Motos: {motos.length} • Otros: {otros.length}
                        </p>
                      </div>
                      <Section title="Autos" data={autos} />
                      <Section title="Motos" data={motos} />
                      <Section title="Otros" data={otros} />
                    </div>
                  );
                })()}

                {/* No results message */}
                {searchPerformed && !searching && searchResults.length === 0 && (
                  <div className={`bg-white rounded-lg shadow-md p-8 text-center ${isMonitoreo ? 'w-full max-w-3xl' : ''}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mt-4">No se encontraron tickets</h3>
                    <p className="text-gray-500 mt-2">
                      No hay tickets para la placa {searchQuery} en la fecha {searchDate}
                      {isMonitoreo && searchType === 'plate_moto' && ' (moto)'}
                      {isMonitoreo && searchType === 'plate_auto' && ' (auto)'}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">
                      Intente con otra fecha, placa o ID de ticket
                    </p>
                  </div>
                )}
                </div>
              </>
          </main>
        </div>
      </div>
    </div>
  );
}

export default TicketSearch;
